/**
 * 將 Redux 巢狀 permissions 轉成帳號權限管理 UI 用的 checkbox 樹。
 * 邏輯與 Options/Permissions.js 編輯彈窗載入後一致（唯讀展示用）。
 */

import jwtDecode from "jwt-decode";
import { isTestBranchName } from "./sortBranchesPutTestLast";

const PERMISSION_SUBPAGE_ORDER_BY_CATEGORY = {
  數據: [
    "業績報表",
    "諮詢師業績",
    "美容顧問業績",
    "訂單業績明細",
    "個人分潤",
  ],
  銷售: ["課程結帳", "儲值金結帳", "結帳紀錄", "診所營運戰情室"],
  會員: ["會員列表", "諮詢紀錄", "初診單管理", "文件批次列印", "簽署中心", "問診單", "群發訊息/課程", "發送訊息", "轉贈紀錄", "換購紀錄"],
  預約: ["預約行事曆"],
  課程: ["課程管理"],
  班表: [
    "團隊成員",
    "經紀人",
    "班別設定",
    "班表管理",
    "服務上架",
    "分類管理",
    "服務分類管理",
    "成本與分潤",
    "修改密碼",
    "範本設定",
    "設定服務分類",
    "服務關聯設定",
    "付款同意書",
    "療程後確認單",
  ],
  設定: [
    "公司資訊",
    "店家資訊",
    "診間設定",
    "標籤管理",
    "等級設定",
    "贈品規則",
    "初診單設定",
    "病歷管理",
    "自動化訊息設定",
    "帳號權限管理",
    "帳號管理權限",
    "POS付款/發票設定",
    "隱碼設定",
  ],
  帳務: [
    "櫃檯人員日結",
    "關帳人員日結",
    "日結記錄",
    "日結紀錄",
    "現金提領",
    "帳務審核",
    "欠款明細",
  ],
  庫存: [
    "廠商管理",
    "商品廠商價格",
    "請購單",
    "請購單系統",
    "請購單列表",
    "庫存管理列表",
    "出/入庫紀錄",
    "盤點單",
    "盤點單列表",
    "供應商分配",
    "採購單列表",
    "驗收單列表",
    "倉庫管理",
  ],
};

const UI_ONLY_HIDDEN_PERMISSION_SECOND_LEVEL = new Set([
  "儲值金結帳",
  "欠款明細",
  "盤點單列表",
]);

/** 與 Permissions.js 一致：第三層無主流程入口的權限方法（目前為空，預留擴充） */
const UI_ONLY_HIDDEN_PERMISSION_THIRD_LEVEL = new Set([]);

/** API 尚未回傳「經紀人」時補上（與 Permissions.js initialSchedule 一致） */
const SCHEDULE_BROKER_PERMISSION_TEMPLATE = {
  label: "經紀人",
  children: [{ label: "查看", id: [125] }],
};

const INVENTORY_PURCHASE_REQUISITION_METHOD_SUPPLEMENTS = [
  { label: "初審", id: [126] },
  { label: "複審", id: [127] },
];

const INVENTORY_SUPPLIER_ALLOCATION_SUPPLEMENT = {
  label: "供應商分配",
  children: [
    { label: "查看-以商品為主體", id: [138] },
    { label: "編輯", id: [139] },
    { label: "查看-以供應商為主體", id: [140] },
  ],
};

const INVENTORY_ITEM_PRICES_COST_SUPPLEMENT = { label: "成本", id: [160] };

const INVENTORY_STOCKTAKE_SUPPLEMENT = {
  label: "盤點單",
  children: [
    { label: "查看", id: [162] },
    { label: "新增", id: [163] },
    { label: "暫存實盤資料", id: [164] },
    { label: "刪除", id: [165] },
    { label: "盤點完成", id: [166] },
    { label: "查看差異報告", id: [167] },
    { label: "校閱暫存", id: [168] },
    { label: "送出審核", id: [169] },
    { label: "審核退回", id: [170] },
    { label: "審核通過", id: [171] },
    { label: "作廢審核通過", id: [172] },
  ],
};

/**
 * 隱碼功能虛擬權限 IDs（對應 functions 資料表 ID）
 * 173: 開啟功能 (總開關), 174: 全顯示, 175: 需手動, 176: 顯示部分
 */
export const MASKING_MODE_FUNCTION_IDS = {
  SWITCH: 173,
  SHOW: 174,
  MANUAL: 175,
  HIDDEN: 176,
};

/** 隱碼設定三選一的子選項 IDs（互斥群組） */
export const MASKING_MODE_OPTION_IDS = [
  MASKING_MODE_FUNCTION_IDS.SHOW,
  MASKING_MODE_FUNCTION_IDS.MANUAL,
  MASKING_MODE_FUNCTION_IDS.HIDDEN,
];

/** API 可能未回傳「隱碼設定」時的補充資料（與 functions 資料表一致） */
const SETTINGS_MASKING_MODE_SUPPLEMENT = {
  label: "隱碼設定",
  children: [
    { label: "開啟功能", id: [MASKING_MODE_FUNCTION_IDS.SWITCH] },
    { label: "全顯示",   id: [MASKING_MODE_FUNCTION_IDS.SHOW] },
    { label: "需手動",   id: [MASKING_MODE_FUNCTION_IDS.MANUAL] },
    { label: "顯示部分", id: [MASKING_MODE_FUNCTION_IDS.HIDDEN] },
  ],
};

const SETTINGS_PERMISSION_UI_WHITELIST = new Set([
  "公司資訊",
  "店家資訊",
  "診間設定",
  "API串接",
  "標籤管理",
  "等級設定",
  "贈品規則",
  "初診單設定",
  "病歷管理",
  "自動化訊息設定",
  "帳號權限管理",
  "帳號管理權限",
  "POS付款/發票設定",
  "隱碼設定",
]);

const ORDER_PERFORMANCE_LABEL = "訂單業績明細";
const VERIFICATION_REPORT_LABEL = "核銷明細報表";
const BEAUTY_CONSULTANT_PERFORMANCE_LABEL = "美容顧問業績";
const CONSULTANT_PERFORMANCE_LABEL = "諮詢師業績";

const CATEGORY_DISPLAY_ORDER = [
  { key: "數據", label: "數據" },
  { key: "銷售", label: "銷售" },
  { key: "會員", label: "會員" },
  { key: "預約", label: "預約" },
  { key: "課程", label: "課程" },
  { key: "班表", label: "班表" },
  { key: "帳務", label: "帳務" },
  { key: "設定", label: "設定" },
  { key: "庫存", label: "請採驗" },
];

const METHOD_SORT_ORDER = {
  查看: 1,
  "查看-以商品為主體": 1.5,
  新增: 2,
  編輯: 3,
  回饋金管理: 3.01,
  紅利點數管理: 3.02,
  綁定諮詢師: 3.03,
  綁定經紀人: 3.04,
  變更會員等級: 3.05,
  刪除: 3.4,
  成本: 3.5,
  匯出會員: 3.7,
  匯出結帳紀錄: 3.72,
  匯出業績: 3.74,
  匯出庫存: 3.725,
  "匯出出/入庫紀錄及明細": 3.726,
  審核: 4,
  初審: 5,
  複審: 6,
  出貨: 7,
  入庫: 8,
  "查看差異報告": 8.2,
  "暫存實盤資料": 8.3,
  盤點完成: 8.4,
  校閱暫存: 8.5,
  送出審核: 8.6,
  審核退回: 8.7,
  審核通過: 8.8,
  作廢審核通過: 8.9,
  "查看-以供應商為主體": 9,
  查看全部預約: 9.5,
  新增預約: 10,
  編輯預約: 11,
  取消預約: 12,
  關帳: 13,
  關帳紀錄: 14,
  療程紀錄模板: 14.1,
  療程紀錄查看: 14.2,
};

function sortPermissionCategorySecondLevelChildren(categoryLabel, children) {
  const order = PERMISSION_SUBPAGE_ORDER_BY_CATEGORY[categoryLabel];
  if (!order || !children?.length) return;
  children.sort((a, b) => {
    const ia = order.indexOf(a.label);
    const ib = order.indexOf(b.label);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
}

function sortMethodChildren(children) {
  if (!children?.length) return;
  children.sort(
    (a, b) =>
      (METHOD_SORT_ORDER[a.label] || 99) - (METHOD_SORT_ORDER[b.label] || 99)
  );
}

function nestedPermissionsToFlatArray(permissions) {
  const result = [];
  if (!permissions || typeof permissions !== "object") return result;

  Object.entries(permissions).forEach(([category, categoryData]) => {
    if (category === "enable" || !categoryData || typeof categoryData !== "object") {
      return;
    }
    Object.entries(categoryData).forEach(([name, nameData]) => {
      if (name === "enable" || !nameData || typeof nameData !== "object") return;
      Object.entries(nameData).forEach(([method, methodData]) => {
        if (method === "enable") return;
        if (!methodData || typeof methodData !== "object") return;
        result.push({
          category,
          name,
          method,
          enable: isPermissionEnabled(methodData.enable) ? "true" : "false",
          functionIds: methodData.functionIds || methodData.function_ids || [],
        });
      });
    });
  });

  return result;
}

function buildCategoryMapFromFlatPermissions(flatPermissions) {
  const categoryMap = (flatPermissions || []).reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = {
        label: item.category,
        checked: false,
        children: [],
      };
    }

    let nameObj = acc[item.category].children.find(
      (child) => child.label === item.name
    );
    if (!nameObj) {
      nameObj = {
        label: item.name,
        checked: false,
        children: [],
      };
      acc[item.category].children.push(nameObj);
    }

    const methodObj = {
      label: item.method,
      checked: item.enable === "true",
      id: item.functionIds,
    };

    nameObj.children.push(methodObj);
    nameObj.checked = nameObj.checked || methodObj.checked;
    acc[item.category].checked =
      acc[item.category].checked || nameObj.checked;

    return acc;
  }, {});

  return categoryMap;
}

function hidePurchaseRequisitionLegacyApprove(categoryMap) {
  const inventory = categoryMap["庫存"];
  if (!inventory) return;
  const purchaseRequisition = inventory.children.find(
    (c) => c.label === "請購單"
  );
  if (!purchaseRequisition) return;
  purchaseRequisition.children.forEach((m) => {
    if (m.label === "審核") m.hidden = true;
  });
}

function hideUnusedPermissionLabelsUiOnly(categoryMap) {
  Object.values(categoryMap).forEach((cat) => {
    cat.children?.forEach((c) => {
      if (UI_ONLY_HIDDEN_PERMISSION_SECOND_LEVEL.has(c.label)) c.hidden = true;
      c.children?.forEach((m) => {
        if (UI_ONLY_HIDDEN_PERMISSION_THIRD_LEVEL.has(m.label)) m.hidden = true;
      });
    });
  });
}

function ensureScheduleBrokerPermissionRow(scheduleCat) {
  if (!scheduleCat?.children) return;
  if (scheduleCat.children.some((c) => c.label === SCHEDULE_BROKER_PERMISSION_TEMPLATE.label)) {
    return;
  }
  scheduleCat.children.push({
    label: SCHEDULE_BROKER_PERMISSION_TEMPLATE.label,
    checked: false,
    children: SCHEDULE_BROKER_PERMISSION_TEMPLATE.children.map((m) => ({
      ...m,
      checked: false,
    })),
  });
}

function supplementInventoryPermissionRows(inventoryCategory) {
  if (!inventoryCategory?.children) return;

  const purchaseRequisition = inventoryCategory.children.find(
    (child) => child.label === "請購單"
  );
  if (purchaseRequisition) {
    INVENTORY_PURCHASE_REQUISITION_METHOD_SUPPLEMENTS.forEach(({ label, id }) => {
      if (!purchaseRequisition.children.find((c) => c.label === label)) {
        purchaseRequisition.children.push({ label, checked: false, id });
      }
    });
  }

  if (!inventoryCategory.children.find((child) => child.label === "供應商分配")) {
    inventoryCategory.children.push({
      label: INVENTORY_SUPPLIER_ALLOCATION_SUPPLEMENT.label,
      checked: false,
      children: INVENTORY_SUPPLIER_ALLOCATION_SUPPLEMENT.children.map((m) => ({
        ...m,
        checked: false,
      })),
    });
  }

  const itemPrices = inventoryCategory.children.find(
    (child) => child.label === "商品廠商價格"
  );
  if (itemPrices) {
    const { label, id } = INVENTORY_ITEM_PRICES_COST_SUPPLEMENT;
    if (!itemPrices.children.find((c) => c.label === label)) {
      itemPrices.children.push({ label, checked: false, id });
    }
  }

  if (!inventoryCategory.children.find((child) => child.label === "盤點單")) {
    inventoryCategory.children.push({
      label: INVENTORY_STOCKTAKE_SUPPLEMENT.label,
      checked: false,
      children: INVENTORY_STOCKTAKE_SUPPLEMENT.children.map((m) => ({
        ...m,
        checked: false,
      })),
    });
  }
}

/**
 * 與帳號權限管理「編輯／新增」載入邏輯一致：補齊 API 可能缺少的權限列。
 */
export function supplementPermissionCategoryMapForEditUi(categoryMap) {
  if (!categoryMap || typeof categoryMap !== "object") return;
  ensureScheduleBrokerPermissionRow(categoryMap["班表"]);
  supplementInventoryPermissionRows(categoryMap["庫存"]);
}

function applyNestedPermissionsToCategoryMap(categoryMap, nestedPermissions) {
  const userFlat = nestedPermissionsToFlatArray(nestedPermissions);
  const enabledByTriple = new Map();
  userFlat.forEach((item) => {
    if (!item.category || !item.name || !item.method) return;
    enabledByTriple.set(
      `${item.category}\0${item.name}\0${item.method}`,
      isPermissionEnabled(item.enable)
    );
  });

  Object.entries(categoryMap).forEach(([categoryKey, category]) => {
    (category.children || []).forEach((nameNode) => {
      (nameNode.children || []).forEach((methodNode) => {
        const key = `${categoryKey}\0${nameNode.label}\0${methodNode.label}`;
        methodNode.checked = enabledByTriple.get(key) === true;
      });
    });
  });
}

function recomputeCategoryMapCheckedState(categoryMap) {
  Object.values(categoryMap).forEach((category) => {
    (category.children || []).forEach((nameNode) => {
      nameNode.checked = (nameNode.children || []).some(
        (methodNode) => methodNode.checked && !methodNode.hidden
      );
    });
    category.checked = (category.children || []).some(
      (nameNode) => nameNode.checked && !nameNode.hidden
    );
  });
}

function applyMemberPermissionUi(membersCat) {
  if (!membersCat?.children) return;
  membersCat.children.forEach((c) => {
    if (c.label === "群發訊息/課程") c.displayLabel = "發送訊息";
  });
}

/** 班表・術前同意書（API 鍵名仍為 label，僅權限勾選 UI 加前綴） */
const PRE_TREATMENT_CONSENT_PERMISSION_LABELS = new Set([
  "範本設定",
  "設定服務分類",
  "服務關聯設定",
]);

function applySchedulePermissionUi(scheduleCat) {
  if (!scheduleCat?.children) return;
  scheduleCat.children.forEach((c) => {
    if (c.label === "分類管理") c.displayLabel = "服務分類管理";
    if (PRE_TREATMENT_CONSENT_PERMISSION_LABELS.has(c.label)) {
      c.displayLabel = `術前${c.label}`;
    }
  });
}

function mergeStocktakeListIntoStocktake(invCat) {
  if (!invCat?.children) return invCat;
  const stocktake = invCat.children.find((c) => c.label === "盤點單");
  const legacyList = invCat.children.find((c) => c.label === "盤點單列表");
  if (!stocktake || !legacyList) return invCat;

  const mergedChildren = stocktake.children.map((method) => {
    const legacyMethod = legacyList.children.find((m) => m.label === method.label);
    return {
      ...method,
      checked: method.checked || !!(legacyMethod && legacyMethod.checked),
    };
  });

  const mergedStocktake = {
    ...stocktake,
    checked: mergedChildren.some((m) => m.checked),
    children: mergedChildren,
  };

  const rest = invCat.children.filter(
    (c) => c.label !== "盤點單" && c.label !== "盤點單列表"
  );

  return {
    ...invCat,
    checked: [...rest, mergedStocktake].some((c) => c.checked),
    children: [...rest, mergedStocktake],
  };
}

function applyInventoryPermissionUi(invCat) {
  if (!invCat?.children) return;
  const merged = mergeStocktakeListIntoStocktake(invCat);
  merged.children?.forEach((c) => {
    if (c.label === "請購單") c.displayLabel = "請購單系統";
    if (c.label === "出/入庫紀錄") {
      c.displayLabel = "出 / 入庫紀錄";
      c.children?.forEach((m) => {
        if (m.label === "匯出出/入庫紀錄及明細") {
          m.displayLabel = "匯出紀錄 / 明細";
        }
      });
    }
    if (c.label === "供應商分配") {
      c.children?.forEach((m) => {
        if (m.label === "查看-以商品為主體") {
          m.displayLabel = "複審時查看「選擇供應商」";
        }
        if (m.label === "編輯") {
          m.displayLabel = "複審時編輯「選擇供應商」";
        }
        if (m.label === "查看-以供應商為主體") {
          m.displayLabel = "檢視供應商分配（複審之後）";
        }
      });
    }
  });
  invCat.children = merged.children;
  invCat.checked = merged.checked;
}

function enforceSupplierAllocationDependency(invCat) {
  if (!invCat?.children) return;
  const pr = invCat.children.find((c) => c.label === "請購單");
  const secondReviewChecked =
    pr?.children?.find((c) => c.label === "複審")?.checked === true;
  if (secondReviewChecked) return;
  const sa = invCat.children.find((c) => c.label === "供應商分配");
  if (!sa) return;
  sa.checked = false;
  sa.children?.forEach((m) => {
    m.checked = false;
  });
}

const SUPPLIER_ALLOCATION_SUBMODULE_LABEL = "供應商分配";
const STANDARD_VIEW_METHOD_LABEL = "查看";
const SUPPLIER_VIEW_BY_PRODUCT_LABEL = "查看-以商品為主體";
const SUPPLIER_VIEW_BY_SUPPLIER_LABEL = "查看-以供應商為主體";

/** 第三層是否為「查看」類權限（含供應商分配兩種獨立查看） */
export function isPermissionViewMethod(subModuleLabel, methodLabel) {
  if (methodLabel === STANDARD_VIEW_METHOD_LABEL) return true;
  if (subModuleLabel === SUPPLIER_ALLOCATION_SUBMODULE_LABEL) {
    return (
      methodLabel === SUPPLIER_VIEW_BY_PRODUCT_LABEL ||
      methodLabel === SUPPLIER_VIEW_BY_SUPPLIER_LABEL
    );
  }
  return false;
}

/**
 * 同一子模組內：非查看權限勾選時自動勾選對應查看；取消查看時連帶取消依賴它的操作權限。
 * 供應商分配例外：編輯僅綁定「查看-以商品為主體」；「查看-以供應商為主體」獨立存在。
 *
 * @param {object} [lastToggled] - 使用者剛操作的第三層：{ subModuleLabel, methodLabel, checked }
 */
export function enforceViewPermissionBindingsOnSubModule(subModule, lastToggled) {
  if (!subModule?.children?.length) return subModule;

  const children = subModule.children.map((method) => ({ ...method }));
  const isToggledHere =
    lastToggled?.subModuleLabel === subModule.label ? lastToggled : null;

  if (subModule.label === SUPPLIER_ALLOCATION_SUBMODULE_LABEL) {
    const viewByProduct = children.find(
      (method) => method.label === SUPPLIER_VIEW_BY_PRODUCT_LABEL
    );
    const edit = children.find((method) => method.label === "編輯");

    if (
      isToggledHere?.methodLabel === SUPPLIER_VIEW_BY_PRODUCT_LABEL &&
      isToggledHere.checked === false &&
      edit
    ) {
      edit.checked = false;
    } else if (
      isToggledHere?.methodLabel === "編輯" &&
      isToggledHere.checked === true &&
      viewByProduct
    ) {
      viewByProduct.checked = true;
    } else if (edit?.checked && viewByProduct) {
      viewByProduct.checked = true;
    }
  } else {
    const standardView = children.find(
      (method) => method.label === STANDARD_VIEW_METHOD_LABEL
    );
    if (standardView) {
      if (
        isToggledHere?.methodLabel === STANDARD_VIEW_METHOD_LABEL &&
        isToggledHere.checked === false
      ) {
        children.forEach((method) => {
          if (!isPermissionViewMethod(subModule.label, method.label)) {
            method.checked = false;
          }
        });
      } else if (
        isToggledHere &&
        isToggledHere.methodLabel !== STANDARD_VIEW_METHOD_LABEL &&
        isToggledHere.checked === true
      ) {
        standardView.checked = true;
      } else {
        const anyNonViewChecked = children.some(
          (method) =>
            !isPermissionViewMethod(subModule.label, method.label) && method.checked
        );
        if (anyNonViewChecked) {
          standardView.checked = true;
        }
      }
    }
  }

  return {
    ...subModule,
    checked: children.some((method) => method.checked),
    children,
  };
}

export function enforceViewPermissionBindingsOnCategory(category, lastToggled) {
  if (!category?.children) return category;
  const children = category.children.map((subModule) =>
    enforceViewPermissionBindingsOnSubModule(subModule, lastToggled)
  );
  return {
    ...category,
    checked: children.some((subModule) => subModule.checked),
    children,
  };
}

export function enforceViewPermissionBindingsOnCategoryMap(categoryMap) {
  if (!categoryMap || typeof categoryMap !== "object") return categoryMap;
  Object.keys(categoryMap).forEach((key) => {
    categoryMap[key] = enforceViewPermissionBindingsOnCategory(categoryMap[key]);
  });
  return categoryMap;
}

function ensureSettingsMaskingModeRow(settingsCat) {
  if (!settingsCat?.children) return;
  if (settingsCat.children.some((c) => c.label === SETTINGS_MASKING_MODE_SUPPLEMENT.label)) return;
  settingsCat.children.push({
    label: SETTINGS_MASKING_MODE_SUPPLEMENT.label,
    checked: false,
    children: SETTINGS_MASKING_MODE_SUPPLEMENT.children.map((m) => ({
      ...m,
      checked: false,
    })),
  });
}

function applySettingsPermissionUi(settingsCat) {
  if (!settingsCat?.children) return;
  ensureSettingsMaskingModeRow(settingsCat);
  settingsCat.children.forEach((c) => {
    if (!SETTINGS_PERMISSION_UI_WHITELIST.has(c.label)) c.hidden = true;
    if (c.label === "帳號管理權限") c.displayLabel = "帳號權限管理";
    if (c.label === "公司資訊" && c.children) {
      c.children.forEach((m) => {
        if (m.label === "查看") m.hidden = true;
      });
    }
    // 隱碼設定：「開啟功能」本身不需顯示（總開關由父層 checkbox 扮演），僅展示三個模式選項
    if (c.label === "隱碼設定" && c.children) {
      c.children.forEach((m) => {
        if (m.label === "開啟功能") m.hidden = true;
      });
    }
  });
}

function mergeVerificationIntoOrderPerformance(stat) {
  if (!stat?.children?.length) return stat;
  const verIdx = stat.children.findIndex(
    (c) => c.label === VERIFICATION_REPORT_LABEL
  );
  const orderIdx = stat.children.findIndex(
    (c) => c.label === ORDER_PERFORMANCE_LABEL
  );
  const ver = verIdx >= 0 ? stat.children[verIdx] : null;
  const order = orderIdx >= 0 ? stat.children[orderIdx] : null;
  if (!ver && !order) return stat;

  let mergedOrder;
  if (order && ver) {
    mergedOrder = {
      ...order,
      checked: order.checked || ver.checked,
      layerTwoSubline: "核銷明細報表",
      children: order.children.map((m) => {
        const vm = ver.children.find((v) => v.label === m.label);
        return { ...m, checked: m.checked || !!(vm && vm.checked) };
      }),
    };
  } else if (order) {
    mergedOrder = { ...order, layerTwoSubline: "核銷明細報表" };
  } else {
    mergedOrder = {
      ...ver,
      label: ORDER_PERFORMANCE_LABEL,
      layerTwoSubline: "核銷明細報表",
    };
  }

  const rest = stat.children.filter(
    (c) =>
      c.label !== VERIFICATION_REPORT_LABEL &&
      c.label !== ORDER_PERFORMANCE_LABEL
  );
  const nextChildren = [...rest, mergedOrder];
  return {
    ...stat,
    checked: nextChildren.some((c) => c.checked),
    children: nextChildren,
  };
}

function mergeConsultantPerformanceFromBeautyAdvisor(stat) {
  if (!stat?.children) return stat;
  const consultant = stat.children.find(
    (c) => c.label === CONSULTANT_PERFORMANCE_LABEL
  );
  const beauty = stat.children.find(
    (c) => c.label === BEAUTY_CONSULTANT_PERFORMANCE_LABEL
  );
  if (!consultant || !beauty) return stat;
  const newConsultantChildren = consultant.children.map((cm) => {
    const bm = beauty.children.find((b) => b.label === cm.label);
    return { ...cm, checked: cm.checked || !!(bm && bm.checked) };
  });
  const consultantAny = newConsultantChildren.some((x) => x.checked);
  const newChildren = stat.children.map((c) => {
    if (c.label === CONSULTANT_PERFORMANCE_LABEL) {
      return {
        ...c,
        checked: consultantAny,
        children: newConsultantChildren,
      };
    }
    return c;
  });
  return {
    ...stat,
    checked: newChildren.some((c) => c.checked),
    children: newChildren,
  };
}

function syncBeautyAdvisorPerformanceToConsultant(stat) {
  if (!stat?.children) return stat;
  const consultant = stat.children.find(
    (c) => c.label === CONSULTANT_PERFORMANCE_LABEL
  );
  const beauty = stat.children.find(
    (c) => c.label === BEAUTY_CONSULTANT_PERFORMANCE_LABEL
  );
  if (!consultant || !beauty) return stat;
  const updatedBeautyChildren = beauty.children.map((bm) => {
    const cm = consultant.children.find((c) => c.label === bm.label);
    return { ...bm, checked: !!(cm && cm.checked) };
  });
  const beautyAny = updatedBeautyChildren.some((x) => x.checked);
  const newChildren = stat.children.map((c) => {
    if (c.label === BEAUTY_CONSULTANT_PERFORMANCE_LABEL) {
      return { ...c, checked: beautyAny, children: updatedBeautyChildren };
    }
    return c;
  });
  return {
    ...stat,
    checked: newChildren.some((c) => c.checked),
    children: newChildren,
  };
}

function hideBeautyAdvisorPerformanceRow(stat) {
  if (!stat?.children) return stat;
  return {
    ...stat,
    children: stat.children.map((c) =>
      c.label === BEAUTY_CONSULTANT_PERFORMANCE_LABEL
        ? { ...c, hidden: true }
        : c
    ),
  };
}

function prepareStatisticForPermissionUi(stat) {
  if (!stat) return stat;
  const merged = mergeVerificationIntoOrderPerformance(stat);
  sortPermissionCategorySecondLevelChildren("數據", merged.children);
  return hideBeautyAdvisorPerformanceRow(
    syncBeautyAdvisorPerformanceToConsultant(
      mergeConsultantPerformanceFromBeautyAdvisor(merged)
    )
  );
}

function applyCategoryUiTransforms(categoryMap) {
  hidePurchaseRequisitionLegacyApprove(categoryMap);
  hideUnusedPermissionLabelsUiOnly(categoryMap);
  applyMemberPermissionUi(categoryMap["會員"]);
  applySchedulePermissionUi(categoryMap["班表"]);
  applyInventoryPermissionUi(categoryMap["庫存"]);
  enforceSupplierAllocationDependency(categoryMap["庫存"]);
  enforceViewPermissionBindingsOnCategoryMap(categoryMap);
  applySettingsPermissionUi(categoryMap["設定"]);

  Object.values(categoryMap).forEach((category) => {
    sortPermissionCategorySecondLevelChildren(category.label, category.children);
    (category.children || []).forEach((nameObj) => {
      if (nameObj.children?.length) sortMethodChildren(nameObj.children);
    });
  });
}

function isPermissionEnabled(enable) {
  return enable === true || enable === "true";
}

function buildPermissionTreesFromCategoryMap(categoryMap) {
  return CATEGORY_DISPLAY_ORDER.map(({ key, label }) => {
    const node = categoryMap[key];
    if (!node) return null;
    const prepared =
      key === "數據"
        ? prepareStatisticForPermissionUi({ ...node, label })
        : { ...node, label: key === "庫存" ? label : node.label };
    if (key === "庫存") prepared.label = label;
    return prepared;
  }).filter(Boolean);
}

export function buildPermissionCheckboxTreesFromFlatPermissions(flatPermissions) {
  const categoryMap = buildCategoryMapFromFlatPermissions(flatPermissions || []);
  supplementPermissionCategoryMapForEditUi(categoryMap);
  applyCategoryUiTransforms(categoryMap);
  return buildPermissionTreesFromCategoryMap(categoryMap);
}

/**
 * 解析用於「我的帳號權限」的權限範本角色（與編輯權限相同資料來源）。
 */
export function resolvePermissionTemplateRoleRow(
  roles,
  nestedPermissions,
  { roleId } = {}
) {
  if (!Array.isArray(roles) || !roles.length) return null;
  if (roleId != null && roleId !== "") {
    const byId = findRoleRowById(roles, roleId);
    if (byId) return byId;
  }
  const matched = findRoleRowByPermissionMatch(roles, nestedPermissions);
  if (matched) return matched;
  return roles[0];
}

/**
 * 我的帳號權限：以編輯權限的完整範本為底，套用登入 Redux 的實際勾選狀態。
 * 無範本時退回僅顯示 Redux 巢狀權限。
 */
export function buildMyAccountPermissionCheckboxTrees(
  flatTemplatePermissions,
  nestedUserPermissions
) {
  if (!flatTemplatePermissions?.length) {
    return buildPermissionCheckboxTreesFromNested(nestedUserPermissions);
  }

  const categoryMap = buildCategoryMapFromFlatPermissions(flatTemplatePermissions);
  supplementPermissionCategoryMapForEditUi(categoryMap);
  applyNestedPermissionsToCategoryMap(categoryMap, nestedUserPermissions);
  recomputeCategoryMapCheckedState(categoryMap);
  applyCategoryUiTransforms(categoryMap);
  return buildPermissionTreesFromCategoryMap(categoryMap);
}

function collectEnabledLeafTriplesFromTrees(trees) {
  const triples = new Set();
  (trees || []).forEach((categoryNode) => {
    if (categoryNode?.hidden) return;
    const category = categoryNode.label;
    (categoryNode.children || []).forEach((nameNode) => {
      if (nameNode.hidden) return;
      const name = nameNode.label;
      (nameNode.children || []).forEach((methodNode) => {
        if (methodNode.hidden || methodNode.sectionHeaderOnly) return;
        if (methodNode.checked) {
          triples.add(`${category}\0${name}\0${methodNode.label}`);
        }
      });
    });
  });
  return triples;
}

/**
 * @param {object} nestedPermissions Redux user.permissions
 * @returns {Array<{label:string, checked:boolean, children:Array}>}
 */
export function buildPermissionCheckboxTreesFromNested(nestedPermissions) {
  const flat = nestedPermissionsToFlatArray(nestedPermissions);
  const categoryMap = buildCategoryMapFromFlatPermissions(flat);
  applyCategoryUiTransforms(categoryMap);
  return buildPermissionTreesFromCategoryMap(categoryMap);
}

export function resolveUserDisplayName({ employeeInfo, branchStatus, brandInfo }) {
  const name =
    employeeInfo?.employeeName ||
    employeeInfo?.name ||
    employeeInfo?.displayName ||
    employeeInfo?.userName;
  if (name) return String(name).trim();
  if (branchStatus === "admin" && brandInfo?.brand_name) {
    return brandInfo.brand_name;
  }
  return "使用者";
}

const SYSTEM_DEFAULT_ROLE_NAMES = new Set([
  "default",
  "(預設權限)",
  "（預設權限）",
]);

export function formatPermissionRoleDisplayName(roleName) {
  if (roleName == null || String(roleName).trim() === "") return null;
  const normalizedName = String(roleName).trim();
  if (SYSTEM_DEFAULT_ROLE_NAMES.has(normalizedName)) {
    return "default（預設權限）";
  }
  return normalizedName;
}

export function isSystemDefaultPermissionRole(roleName) {
  if (roleName == null) return false;
  return SYSTEM_DEFAULT_ROLE_NAMES.has(String(roleName).trim());
}

/** 從分店角色列表找出系統預設角色（default / 預設權限） */
export function findSystemDefaultPermissionRole(permissionRoles) {
  if (!Array.isArray(permissionRoles)) return null;
  return (
    permissionRoles.find((row) =>
      isSystemDefaultPermissionRole(row?.role?.roleName)
    ) || null
  );
}

/**
 * 品牌管理員（JWT role === admin）不走員工 branch_roles，固定對應系統 default 角色。
 */
export function resolveAdminPermissionRoleDisplay(permissionRoles) {
  const defaultRow = findSystemDefaultPermissionRole(permissionRoles);
  if (defaultRow?.role?.roleName) {
    return formatPermissionRoleDisplayName(defaultRow.role.roleName);
  }
  return formatPermissionRoleDisplayName("default");
}

/**
 * 取出後端該角色目前已啟用的 functionIds（去重）。
 * 純改名儲存時原樣送回，避免前端重建 checkbox 樹漏掉未映射類別而洗掉既有權限。
 */
export function collectEnabledFunctionIds(flatPermissions) {
  const ids = new Set();
  (flatPermissions || []).forEach((item) => {
    if (item.enable !== "true" && item.enable !== true) return;
    const raw = item.functionIds ?? item.function_ids;
    if (raw == null) return;
    (Array.isArray(raw) ? raw : [raw]).forEach((id) => {
      const num = Number(id);
      if (!Number.isNaN(num)) ids.add(num);
    });
  });
  return ids;
}

/**
 * 從單一 checkbox 樹節點收集已勾選 functionIds。
 * node.hidden 仍會收集；statisticMirrorOf / sectionHeaderOnly 略過。
 */
export function extractCheckedIdsFromPermissionTree(node, result = []) {
  if (!node || typeof node !== "object") return result;
  if (node.statisticMirrorOf || node.sectionHeaderOnly) {
    return result;
  }
  if (node.checked && node.id) {
    result.push(...node.id);
  }
  if (node.children) {
    for (const child of node.children) {
      extractCheckedIdsFromPermissionTree(child, result);
    }
  }
  return result;
}

/**
 * 從 modal 狀態的各 checkbox 樹收集已勾選 functionIds（略過 positionName）。
 */
export function collectCheckedIdsFromPermissionTrees(modalState) {
  if (!modalState || typeof modalState !== "object") return [];
  const { positionName, ...trees } = modalState;
  const ids = [];
  Object.values(trees).forEach((tree) => {
    if (tree) extractCheckedIdsFromPermissionTree(tree, ids);
  });
  return ids;
}

/**
 * 以後端啟用清單為底，只套用編輯器勾選差異：
 * final = (serverEnabled − snapshotChecked) ∪ currentChecked
 * → 未出現在開啟時勾選集合的後端 id（含 UI 未映射）會被保留。
 */
export function mergePermissionFunctionIds(serverEnabledIds, snapshotCheckedIds, currentCheckedIds) {
  const merged = new Set(serverEnabledIds || []);
  const snap = new Set(snapshotCheckedIds || []);
  const curr = new Set(currentCheckedIds || []);
  snap.forEach((id) => {
    if (!curr.has(id)) merged.delete(id);
  });
  curr.forEach((id) => merged.add(id));
  return [...merged];
}

/** 以 category + name + method 比對（Redux 巢狀權限通常沒有 functionIds） */
function collectEnabledPermissionTriples(flatPermissions) {
  const triples = new Set();
  (flatPermissions || []).forEach((item) => {
    if (item.enable !== "true" && item.enable !== true) return;
    if (!item.category || !item.name || !item.method) return;
    triples.add(`${item.category}\0${item.name}\0${item.method}`);
  });
  return triples;
}

function permissionTriplesEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const t of a) {
    if (!b.has(t)) return false;
  }
  return true;
}

function permissionIdSetsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function findRoleRowByPermissionMatch(roles, nestedPermissions) {
  if (!Array.isArray(roles) || !roles.length) return null;

  const userTrees = buildPermissionCheckboxTreesFromNested(nestedPermissions);
  const targetTriples = collectEnabledLeafTriplesFromTrees(userTrees);
  if (targetTriples.size > 0) {
    let tripleMatch = null;
    roles.forEach((row) => {
      const roleTriples = collectEnabledLeafTriplesFromTrees(
        buildPermissionCheckboxTreesFromFlatPermissions(row?.permissions)
      );
      if (permissionTriplesEqual(targetTriples, roleTriples)) {
        tripleMatch = row;
      }
    });
    if (tripleMatch) return tripleMatch;
  }

  const flat = nestedPermissionsToFlatArray(nestedPermissions);
  const rawTargetTriples = collectEnabledPermissionTriples(flat);
  if (rawTargetTriples.size > 0) {
    let rawTripleMatch = null;
    roles.forEach((row) => {
      const roleTriples = collectEnabledPermissionTriples(row?.permissions);
      if (permissionTriplesEqual(rawTargetTriples, roleTriples)) {
        rawTripleMatch = row;
      }
    });
    if (rawTripleMatch) return rawTripleMatch;
  }

  const targetIds = collectEnabledFunctionIds(flat);
  if (targetIds.size === 0) return null;

  let idMatch = null;
  roles.forEach((row) => {
    const roleIds = collectEnabledFunctionIds(row?.permissions);
    if (permissionIdSetsEqual(targetIds, roleIds)) {
      idMatch = row;
    }
  });
  return idMatch;
}

/**
 * 以目前 Redux permissions 與分店角色列表比對，找出對應的權限名稱。
 */
export function findPermissionRoleNameByPermissions(roles, nestedPermissions) {
  const matched = findRoleRowByPermissionMatch(roles, nestedPermissions);
  return formatPermissionRoleDisplayName(matched?.role?.roleName);
}

function findRoleRowById(permissionRoles, roleId) {
  if (roleId == null || !Array.isArray(permissionRoles)) return null;
  return (
    permissionRoles.find((row) => {
      const id = row?.role?.id ?? row?.role?.roleId;
      return id != null && String(id) === String(roleId);
    }) || null
  );
}

function resolveRoleNameFromJwt(token) {
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return (
      decoded.roleName ??
      decoded.role_name ??
      decoded.permissionRoleName ??
      decoded.permission_role_name ??
      decoded.currentRoleName ??
      decoded.current_role_name ??
      null
    );
  } catch(err) {
    console.log("Error resolving role name from jwt", err);
    return null;
  }
}

function resolveRoleIdFromJwt(token) {
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return (
      decoded.roleId ??
      decoded.role_id ??
      decoded.permissionRoleId ??
      decoded.permission_role_id ??
      decoded.branchRoleId ??
      decoded.branch_role_id ??
      null
    );
  } catch(err) {
    console.log("Error resolving role id from jwt", err);
    return null;
  }
}

function resolveRoleNameById(permissionRoles, roleId) {
  const row = findRoleRowById(permissionRoles, roleId);
  return formatPermissionRoleDisplayName(row?.role?.roleName);
}

/** 從 branchesAndPermissions 的單一分店項目解析目前綁定的角色 */
export function resolveBranchRoleFromBranchPermissionEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  return {
    roleName:
      entry.role_name ??
      entry.roleName ??
      entry.current_role_name ??
      entry.currentRoleName ??
      entry.permission_role_name ??
      entry.permissionRoleName ??
      null,
    roleId:
      entry.role_id ??
      entry.roleId ??
      entry.current_role_id ??
      entry.currentRoleId ??
      entry.permission_role_id ??
      entry.permissionRoleId ??
      entry.assigned_role_id ??
      entry.assignedRoleId ??
      null,
  };
}

/** 從團隊成員 API 回傳的員工資料解析指定分店的角色 */
export function resolveBranchRoleFromEmployeeRecord(employeeRecord, branchId) {
  if (!employeeRecord || branchId == null) return null;

  const topLevelName =
    employeeRecord.roleName ??
    employeeRecord.role_name ??
    employeeRecord.permissionRoleName;
  const topLevelId =
    employeeRecord.roleId ??
    employeeRecord.role_id ??
    employeeRecord.permissionRoleId;

  const branchRoles =
    employeeRecord.branch_roles || employeeRecord.branchRoles || [];
  const currentBranchRole = branchRoles.find(
    (b) => String(b.branchId ?? b.branch_id) === String(branchId)
  );

  return {
    roleName:
      currentBranchRole?.roleName ??
      currentBranchRole?.role_name ??
      currentBranchRole?.permissionRoleName ??
      topLevelName ??
      null,
    roleId:
      currentBranchRole?.roleId ??
      currentBranchRole?.role_id ??
      currentBranchRole?.permissionRoleId ??
      topLevelId ??
      null,
  };
}

/**
 * 依 JWT、員工資料、branch_roles 等來源解析權限名稱（不含 permissions 比對）。
 */
export function resolveUserPermissionRoleName({
  token,
  employeeInfo,
  branchId,
  permissionRoles,
  branchPermissionEntry,
  employeeRecordBranchRole,
}) {
  const jwtName = resolveRoleNameFromJwt(token);
  if (jwtName) return formatPermissionRoleDisplayName(jwtName);

  if (employeeRecordBranchRole?.roleName) {
    return formatPermissionRoleDisplayName(employeeRecordBranchRole.roleName);
  }

  if (branchPermissionEntry?.roleName) {
    return formatPermissionRoleDisplayName(branchPermissionEntry.roleName);
  }

  const direct =
    employeeInfo?.roleName ||
    employeeInfo?.role_name ||
    employeeInfo?.permissionRoleName ||
    employeeInfo?.permission_role_name ||
    employeeInfo?.currentRoleName ||
    employeeInfo?.current_role_name;
  if (direct) return formatPermissionRoleDisplayName(direct);

  const roleIdCandidates = [
    employeeRecordBranchRole?.roleId,
    branchPermissionEntry?.roleId,
    resolveRoleIdFromJwt(token),
    employeeInfo?.roleId,
    employeeInfo?.role_id,
    employeeInfo?.permissionRoleId,
    employeeInfo?.permission_role_id,
  ];

  const branchRoles =
    employeeInfo?.branch_roles || employeeInfo?.branchRoles || [];
  const currentBranchRole = branchRoles.find(
    (b) => String(b.branchId ?? b.branch_id) === String(branchId)
  );
  if (currentBranchRole) {
    const roleLabel =
      currentBranchRole.roleName ||
      currentBranchRole.role_name ||
      currentBranchRole.permissionRoleName;
    if (roleLabel) return formatPermissionRoleDisplayName(roleLabel);

    roleIdCandidates.push(
      currentBranchRole.roleId,
      currentBranchRole.role_id,
      currentBranchRole.permissionRoleId
    );
  }

  for (const roleId of roleIdCandidates) {
    if (roleId == null || roleId === "") continue;
    const matched = resolveRoleNameById(permissionRoles, roleId);
    if (matched) return matched;
  }

  return null;
}

// ----------------------------------------------------------------------------
// 「我的帳號權限」— 多分店列表（PopMyPermissions「查看有權限的分店」）
// 僅組出各店的 branchName / roleName，不載入各店完整 permissions 勾選樹。
// ----------------------------------------------------------------------------

/** 統一分店 id 欄位（branchId | branch_id | id）供比對用 */
function normalizeBranchIdValue(branchLike) {
  if (branchLike == null || typeof branchLike !== "object") return "";
  const id = branchLike.branchId ?? branchLike.branch_id ?? branchLike.id;
  return id == null ? "" : String(id);
}

/** 從 branchesAndPermissions 各分店項目擷取目前帳號綁定的 roleId / roleName */
export function assignedBranchRolesFromPermissionEntries(branchEntries) {
  if (!Array.isArray(branchEntries)) return [];
  return branchEntries
    .map((entry) => {
      const role = resolveBranchRoleFromBranchPermissionEntry(entry);
      if (!role?.roleId && !role?.roleName) return null;
      const rowBranchId = entry.branch_id ?? entry.branchId;
      return {
        branchId: rowBranchId,
        branch_id: rowBranchId,
        branchName: entry.branch_name ?? entry.branchName,
        branch_name: entry.branch_name ?? entry.branchName,
        roleId: role.roleId,
        role_id: role.roleId,
        roleName: role.roleName,
        role_name: role.roleName,
      };
    })
    .filter(Boolean);
}

/** 合併多來源的 branch_roles（登入 employeeInfo、分店員工 API 等），以分店 id 去重 */
export function mergeAssignedBranchRoles(...sources) {
  const merged = new Map();
  sources.forEach((source) => {
    if (!Array.isArray(source)) return;
    source.forEach((item) => {
      const key = normalizeBranchIdValue(item);
      if (!key) return;
      merged.set(key, { ...(merged.get(key) || {}), ...item });
    });
  });
  return [...merged.values()];
}

/** 合併多份 branchesAndPermissions 快取（以分店 id 去重，保留 roles 等欄位） */
export function mergeBranchCatalogEntries(...sources) {
  const byId = new Map();
  sources.forEach((source) => {
    if (!Array.isArray(source)) return;
    source.forEach((entry) => {
      const key = normalizeBranchIdValue(entry);
      if (!key) return;
      byId.set(key, { ...(byId.get(key) || {}), ...entry });
    });
  });
  return [...byId.values()];
}

/** 將 subscribe/getSubscribeBrandInfo 的 branches 轉成與 brandInfo.branches 相同欄位結構 */
export function normalizeSubscribeBranchesForCatalog(subscribeBranches) {
  if (!Array.isArray(subscribeBranches)) return [];
  return subscribeBranches.map((branch) => {
    const id = branch.branch_id ?? branch.id;
    const name = branch.branch_name ?? branch.branchName ?? branch.name;
    return {
      id,
      branch_id: id,
      branchId: id,
      branch_name: name,
      branchName: name,
    };
  });
}

/**
 * 彙整品牌下所有分店（Redux brandInfo、訂閱方案、API、branch_roles、快取），供列表顯示完整清單。
 */
export function buildFullBrandBranchList(
  brandBranches,
  branchEntries,
  assignedBranchRoles,
  extraBrandBranches
) {
  const byId = new Map();
  const addBranch = (raw) => {
    if (!raw || typeof raw !== "object") return;
    const key = normalizeBranchIdValue(raw);
    if (!key) return;
    const prev = byId.get(key) || {};
    byId.set(key, {
      ...prev,
      ...raw,
      id: raw.id ?? raw.branch_id ?? raw.branchId ?? prev.id,
      branch_id: raw.branch_id ?? raw.branchId ?? raw.id ?? prev.branch_id,
      branchId: raw.branchId ?? raw.branch_id ?? raw.id ?? prev.branchId,
      branch_name:
        raw.branch_name ??
        raw.branchName ??
        raw.name ??
        prev.branch_name ??
        prev.branchName,
      branchName:
        raw.branchName ??
        raw.branch_name ??
        prev.branchName ??
        prev.branch_name,
      roles: raw.roles ?? prev.roles ?? [],
    });
  };
  (extraBrandBranches || []).forEach(addBranch);
  (brandBranches || []).forEach(addBranch);
  (branchEntries || []).forEach(addBranch);
  (assignedBranchRoles || []).forEach((roleRow) =>
    addBranch({
      branch_id: roleRow.branchId ?? roleRow.branch_id,
      branchId: roleRow.branchId ?? roleRow.branch_id,
      branch_name: roleRow.branchName ?? roleRow.branch_name,
      branchName: roleRow.branchName ?? roleRow.branch_name,
    })
  );
  return [...byId.values()];
}

/** 以品牌完整分店清單為主，補上 branchesAndPermissions 的 roles 等欄位 */
function buildBranchCatalogEntries(brandBranches, branchEntries) {
  const entries = Array.isArray(branchEntries) ? branchEntries : [];
  if (!Array.isArray(brandBranches) || brandBranches.length === 0) {
    return entries;
  }
  const entryById = new Map(
    entries.map((entry) => [normalizeBranchIdValue(entry), entry])
  );
  return brandBranches.map((branch) => {
    const key = normalizeBranchIdValue(branch);
    const existing = key ? entryById.get(key) : null;
    return {
      ...(existing || {}),
      branch_id: branch.id ?? branch.branch_id ?? existing?.branch_id ?? existing?.branchId,
      branchId: branch.id ?? branch.branchId ?? existing?.branchId ?? existing?.branch_id,
      branch_name:
        branch.branch_name ??
        branch.branchName ??
        existing?.branch_name ??
        existing?.branchName,
      branchName:
        branch.branchName ??
        branch.branch_name ??
        existing?.branchName ??
        existing?.branch_name,
      roles: existing?.roles ?? branch.roles ?? [],
    };
  });
}

/**
 * 解析分店顯示名稱：優先員工 branch_roles → branchesAndPermissions 項目 → 目前 Redux branchInfo
 */
function resolveBranchDisplayName(branchLike, branchEntry, currentBranchInfo) {
  const fromLike =
    branchLike?.branchName ??
    branchLike?.branch_name ??
    branchEntry?.branch_name ??
    branchEntry?.branchName;
  if (fromLike != null && String(fromLike).trim() !== "") {
    return String(fromLike).trim();
  }
  const fromCurrent =
    currentBranchInfo?.branch_name ?? currentBranchInfo?.branchName;
  if (fromCurrent != null && String(fromCurrent).trim() !== "") {
    return String(fromCurrent).trim();
  }
  return "";
}

/** 僅有 roleId 時，從該店 branchesAndPermissions.roles 對照出角色名稱 */
function resolveRoleNameFromBranchCatalog(roleId, branchEntry) {
  if (roleId == null || roleId === "" || !branchEntry?.roles) return null;
  const matched = branchEntry.roles.find((role) => {
    const id = role.role_id ?? role.roleId ?? role.id;
    return id != null && String(id) === String(roleId);
  });
  if (!matched) return null;
  return formatPermissionRoleDisplayName(
    matched.role_name ?? matched.roleName ?? matched.label
  );
}

/** 從任一分店的 roles 清單對照 roleId（跨店比對） */
function resolveRoleNameFromAnyBranchCatalog(roleId, branchEntries) {
  if (roleId == null || roleId === "") return null;
  for (const entry of branchEntries || []) {
    const name = resolveRoleNameFromBranchCatalog(roleId, entry);
    if (name) return name;
  }
  return null;
}

export const MY_ACCOUNT_NO_BRANCH_ROLE_LABEL = "無權限";

/**
 * 組出「我的帳號權限」—「查看有權限的分店」列表資料（分店權限名稱列表）。
 *
 * 以 brandBranches（品牌全部分店）為**完整分店清單**；無則退回 branchEntries。
 * 角色指派來自 assignedBranchRoles（建議合併 employeeInfo + 分店員工 API 的 branch_roles）。
 * 該店無指派時顯示「無權限」。品牌管理員（admin）則各店皆顯示預設權限名稱。
 *
 * @returns {Array<{ branchId, branchName, roleName, isCurrent: boolean, hasPermission: boolean }>}
 *   isCurrent 為 true 表示與參數 branchId（左上角目前分店）相同
 */
export function buildMyAccountBranchPermissionRows({
  branchId,
  currentBranchInfo,
  employeeInfo,
  assignedBranchRoles,
  brandBranches,
  extraBrandBranches,
  branchEntries,
  permissionRoles,
  branchStatus,
}) {
  const currentId = branchId == null ? "" : String(branchId);
  const branchRoles =
    assignedBranchRoles ??
    mergeAssignedBranchRoles(
      employeeInfo?.branch_roles,
      employeeInfo?.branchRoles
    );
  const fullBrandList = buildFullBrandBranchList(
    brandBranches,
    branchEntries,
    branchRoles,
    extraBrandBranches
  );
  const catalogEntries = buildBranchCatalogEntries(fullBrandList, branchEntries);
  const findBranchEntry = (id) =>
    catalogEntries.find((entry) => normalizeBranchIdValue(entry) === String(id));

  const resolveRowRoleName = (branchRole, entry, rowBranchId) => {
    const direct =
      branchRole?.roleName ??
      branchRole?.role_name ??
      branchRole?.permissionRoleName ??
      branchRole?.permission_role_name;
    if (direct) return formatPermissionRoleDisplayName(direct);

    const roleId =
      branchRole?.roleId ??
      branchRole?.role_id ??
      branchRole?.permissionRoleId ??
      branchRole?.permission_role_id;
    const fromCatalog = resolveRoleNameFromBranchCatalog(roleId, entry);
    if (fromCatalog) return fromCatalog;
    const fromAnyCatalog = resolveRoleNameFromAnyBranchCatalog(roleId, catalogEntries);
    if (fromAnyCatalog) return fromAnyCatalog;
    if (rowBranchId != null && String(rowBranchId) === currentId && roleId != null) {
      return resolveRoleNameById(permissionRoles, roleId);
    }
    return null;
  };

  const findAssignedBranchRole = (rowBranchId) =>
    branchRoles.find((b) => normalizeBranchIdValue(b) === String(rowBranchId));

  const hasAssignedRole = (branchRole) => {
    if (!branchRole) return false;
    const roleId =
      branchRole?.roleId ??
      branchRole?.role_id ??
      branchRole?.permissionRoleId ??
      branchRole?.permission_role_id;
    const roleName =
      branchRole?.roleName ??
      branchRole?.role_name ??
      branchRole?.permissionRoleName ??
      branchRole?.permission_role_name;
    return (
      (roleId != null && roleId !== "") ||
      (roleName != null && String(roleName).trim() !== "")
    );
  };

  const isAdmin = branchStatus === "admin";
  const adminRoleName = isAdmin
    ? resolveAdminPermissionRoleDisplay(permissionRoles) || "default（預設權限）"
    : null;

  const buildRow = (rowBranchId, entry, assignedBranchRole) => {
    const branchName =
      resolveBranchDisplayName(assignedBranchRole, entry, currentBranchInfo) ||
      (rowBranchId != null ? `分店 #${rowBranchId}` : "分店");

    const assigned = hasAssignedRole(assignedBranchRole);
    let roleName;
    if (isAdmin) {
      roleName = adminRoleName;
    } else if (assigned) {
      roleName =
        resolveRowRoleName(assignedBranchRole, entry, rowBranchId) || "—";
    } else {
      roleName = MY_ACCOUNT_NO_BRANCH_ROLE_LABEL;
    }

    const hasPermission = isAdmin || assigned;

    return {
      branchId: rowBranchId,
      branchName,
      roleName,
      isCurrent: String(rowBranchId) === currentId,
      hasPermission,
    };
  };

  let rows = [];

  if (catalogEntries.length > 0) {
    rows = catalogEntries.map((entry) => {
      const rowBranchId = entry.branch_id ?? entry.branchId;
      const assigned = findAssignedBranchRole(rowBranchId);
      return buildRow(rowBranchId, entry, assigned);
    });

    const listedIds = new Set(
      rows.map((row) => (row.branchId == null ? "" : String(row.branchId)))
    );
    branchRoles.forEach((branchRole) => {
      const rowBranchId = branchRole.branchId ?? branchRole.branch_id;
      const idKey = rowBranchId == null ? "" : String(rowBranchId);
      if (!idKey || listedIds.has(idKey)) return;
      listedIds.add(idKey);
      rows.push(buildRow(rowBranchId, findBranchEntry(rowBranchId), branchRole));
    });
  } else if (branchRoles.length > 0) {
    rows = branchRoles.map((branchRole) => {
      const rowBranchId = branchRole.branchId ?? branchRole.branch_id;
      return buildRow(rowBranchId, findBranchEntry(rowBranchId), branchRole);
    });
  }

  if (rows.length === 0 && currentId) {
    const entry = findBranchEntry(currentId);
    const assigned = findAssignedBranchRole(currentId);
    rows.push(buildRow(branchId, entry, assigned));
  }

  rows.sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    const aTest = isTestBranchName(a.branchName);
    const bTest = isTestBranchName(b.branchName);
    if (aTest !== bTest) return aTest ? 1 : -1;
    return String(a.branchName).localeCompare(String(b.branchName), "zh-Hant");
  });

  return rows;
}
